/**
 * Sentiment Analysis Page
 * Professional analytics dashboard with grid/list views and detail modal
 */

import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import toast from 'react-hot-toast';
import { AlertTriangle, BarChart3, BookOpen, ChevronDown, ChevronUp, LayoutGrid, List, Search, Filter } from 'lucide-react';
import TrendChart from '../components/dashboard/TrendChart';
import MetricCard from '../components/dashboard/MetricCard';
import DateRangeFilter from '../components/dashboard/DateRangeFilter';
import AnalysisCard from '../components/dashboard/AnalysisCard';
import AnalysisDetailModal, { CATEGORY_COLORS } from '../components/dashboard/AnalysisDetailModal';
import SentimentPieChart from '../components/dashboard/SentimentPieChart';
import ScoreGauge from '../components/dashboard/ScoreGauge';
import RefreshButton from '../components/shared/RefreshButton';
import Pagination from '@shared/components/Pagination';
import useDateRange from '../hooks/useDateRange';
import usePagination from '@shared/hooks/usePagination';
import { getSentimentOverview, getSentimentTrends, getRecentSignals, getAnalysesList, getTopSignals } from '../services/sentimentService';
import useDebounce from '../hooks/useDebounce';
import { toTitleCase } from '../utils/formatters';

const ANALYSIS_TABS = [
  { key: 'all', label: 'All' },
  { key: 'negative', label: 'Attention Needed', icon: AlertTriangle },
];

/* ── Main Page ──────────────────────────────────── */
const SentimentAnalysisPage = () => {
  const { from, to, onChange } = useDateRange();
  const { page, pageSize, setPage, setPageSize } = usePagination(1, 10);

  const [loading, setLoading] = useState(true);
  const [overview, setOverview] = useState(null);
  const [trends, setTrends] = useState([]);
  const [signals, setSignals] = useState([]);
  const [analyses, setAnalyses] = useState([]);
  const [totalAnalyses, setTotalAnalyses] = useState(0);
  const [signalFilter, setSignalFilter] = useState('all');
  const [analysisTab, setAnalysisTab] = useState('all');
  const [topSignals, setTopSignals] = useState([]);
  const [showSignalLibrary, setShowSignalLibrary] = useState(false);
  const [viewMode, setViewMode] = useState('grid');
  const [selectedAnalysis, setSelectedAnalysis] = useState(null);
  const [channelFilter, setChannelFilter] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const debouncedSearch = useDebounce(searchTerm, 400);
  const analysesFetchIdRef = useRef(0);

  const fetchDashboard = useCallback(async () => {
    try {
      setLoading(true);
      const dateRange = { from, to };
      if (channelFilter) dateRange.interaction_type = channelFilter;
      const [overviewRes, trendsRes, signalsRes, topSignalsRes] = await Promise.all([
        getSentimentOverview(dateRange),
        getSentimentTrends(dateRange),
        getRecentSignals(),
        getTopSignals(dateRange),
      ]);
      if (overviewRes.success) setOverview(overviewRes.data);
      else toast.error(overviewRes.error || 'Failed to load sentiment overview');
      if (trendsRes.success) setTrends(trendsRes.data);
      else toast.error(trendsRes.error || 'Failed to load sentiment trends');
      if (signalsRes.success) setSignals(signalsRes.data);
      else toast.error(signalsRes.error || 'Failed to load signals');
      if (topSignalsRes.success) setTopSignals(topSignalsRes.data);
      else toast.error(topSignalsRes.error || 'Failed to load top signals');
    } catch (err) {
      toast.error('Failed to load sentiment data');
    } finally {
      setLoading(false);
    }
  }, [from, to, channelFilter]);

  const fetchAnalyses = useCallback(async () => {
    const fetchId = ++analysesFetchIdRef.current;
    const params = { page, page_size: pageSize, from_date: from, to_date: to };
    if (analysisTab === 'negative') params.sentiment = 'negative,very_negative';
    if (channelFilter) params.interaction_type = channelFilter;
    if (debouncedSearch) params.search = debouncedSearch;
    const result = await getAnalysesList(params);
    if (fetchId !== analysesFetchIdRef.current) return;
    if (result.success) {
      const d = result.data;
      const items = d.analyses || [];
      setAnalyses(items);
      setTotalAnalyses(d.total || 0);
    } else {
      toast.error(result.error || 'Failed to load analyses');
    }
  }, [from, to, page, pageSize, analysisTab, channelFilter, debouncedSearch]);

  useEffect(() => { fetchDashboard(); }, [fetchDashboard]);
  useEffect(() => { fetchAnalyses(); }, [fetchAnalyses]);

  const categories = useMemo(() => {
    const cats = new Set();
    signals.forEach(s => { if (s.signal_category) cats.add(s.signal_category); });
    return ['all', ...Array.from(cats).sort()];
  }, [signals]);

  const filteredSignals = useMemo(() => {
    if (signalFilter === 'all') return signals;
    return signals.filter(s => s.signal_category === signalFilter);
  }, [signals, signalFilter]);

  const negativeCount = overview ? (overview.negative || 0) : 0;

  if (loading) return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center space-x-3">
          <div className="skeleton h-8 w-20 rounded-lg" />
          <div className="skeleton h-6 w-24 rounded-full" />
        </div>
        <div className="flex items-center space-x-3">
          <div className="skeleton h-8 w-32 rounded-lg" />
          <div className="skeleton h-8 w-32 rounded-lg" />
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-12 gap-4 mb-8">
        <div className="md:col-span-4 lg:col-span-3">
          <div className="card p-5 h-48"><div className="skeleton h-full w-full rounded-lg" /></div>
        </div>
        <div className="md:col-span-8 lg:col-span-5">
          <div className="card p-5 h-48"><div className="skeleton h-full w-full rounded-lg" /></div>
        </div>
        <div className="md:col-span-12 lg:col-span-4 space-y-4">
          <div className="card p-5 h-20"><div className="skeleton h-full w-full rounded-lg" /></div>
          <div className="card p-5 h-24"><div className="skeleton h-full w-full rounded-lg" /></div>
        </div>
      </div>
      <div className="card p-5 mb-8">
        <div className="skeleton-title mb-4" />
        <div className="skeleton h-64 w-full rounded-lg" />
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
        {[1, 2, 3, 4].map(i => (
          <div key={i} className="card p-5">
            <div className="flex items-center space-x-3 mb-3">
              <div className="skeleton h-4 w-4 rounded" />
              <div className="skeleton h-4 w-20 rounded" />
              <div className="skeleton h-5 w-16 rounded-full" />
            </div>
            <div className="skeleton-text mb-2" />
            <div className="skeleton h-2 w-full rounded-full" />
          </div>
        ))}
      </div>
    </div>
  );

  return (
    <div>
      {/* Section A: Controls bar */}
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div className="flex items-center space-x-3">
          <RefreshButton onClick={() => { fetchDashboard(); fetchAnalyses(); }} loading={loading} />
          {negativeCount > 0 && (
            <button
              onClick={() => { setAnalysisTab('negative'); setPage(1); }}
              className="flex items-center space-x-1 text-xs text-red-600 bg-red-50 px-2.5 py-1 rounded-full hover:bg-red-100 transition-colors"
            >
              <AlertTriangle size={12} />
              <span>{negativeCount} need attention</span>
            </button>
          )}
          <button
            onClick={() => setShowSignalLibrary(!showSignalLibrary)}
            className="flex items-center space-x-1.5 px-3 py-1.5 text-sm text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
          >
            <BookOpen size={14} />
            <span>Signals ({signals.length})</span>
            {showSignalLibrary ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
          </button>
        </div>
        <div className="flex items-center space-x-3">
          {/* Search */}
          <div className="relative">
            <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => { setSearchTerm(e.target.value); setPage(1); }}
              placeholder="Search interactions..."
              className="pl-8 pr-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500 w-48"
            />
          </div>
          {/* Channel filter */}
          <div className="relative">
            <Filter size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" />
            <select
              value={channelFilter}
              onChange={(e) => { setChannelFilter(e.target.value); setPage(1); }}
              className="pl-8 pr-8 py-1.5 text-sm border border-gray-300 rounded-lg bg-white focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500 appearance-none"
            >
              <option value="">All Channels</option>
              <option value="call">Call</option>
              <option value="chat">Chat</option>
              <option value="email">Email</option>
            </select>
          </div>
          <DateRangeFilter from={from} to={to} onChange={onChange} />
        </div>
      </div>

      {/* Section B: Overview Strip */}
      <div className="grid grid-cols-1 md:grid-cols-12 gap-4 mb-8 items-stretch">
        <div className="md:col-span-4 lg:col-span-3 flex">
          <div className="flex-1">
            <ScoreGauge score={overview?.avg_score || 0} label="Average Sentiment" />
          </div>
        </div>
        <div className="md:col-span-8 lg:col-span-5 flex">
          <div className="flex-1">
            <SentimentPieChart
              positive={overview?.positive || 0}
              neutral={overview?.neutral || 0}
              negative={overview?.negative || 0}
            />
          </div>
        </div>
        <div className="md:col-span-12 lg:col-span-4 flex flex-col gap-4">
          <MetricCard label="Total Analyses" value={overview?.total_analyses ?? '-'} color="#6366f1" />
          <div className="card p-5 flex-1">
            <h3 className="text-sm font-semibold text-gray-700 mb-2">Top Signals</h3>
            {topSignals.length === 0 ? (
              <p className="text-sm text-gray-400">No signals detected yet.</p>
            ) : (
              <div className="space-y-1.5">
                {topSignals.slice(0, 5).map((s, i) => (
                  <div key={s.signal_name} className="flex items-center justify-between text-sm">
                    <div className="flex items-center space-x-2">
                      <span className="text-gray-400 w-4 text-right">{i + 1}.</span>
                      <span className="text-gray-700">{toTitleCase(s.signal_name)}</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <span className={`text-xs px-1.5 py-0.5 rounded ${
                        CATEGORY_COLORS[s.signal_category] || 'bg-gray-100 text-gray-600'
                      }`}>{toTitleCase(s.signal_category)}</span>
                      <span className="text-xs text-gray-500 w-6 text-right">{s.count}</span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Section C: Trend chart (full width) */}
      <div className="mb-8">
        <TrendChart
          title="Daily Sentiment Breakdown"
          data={trends}
          xKey="date"
          lines={[
            { dataKey: 'positive', color: '#22c55e', label: 'Positive' },
            { dataKey: 'neutral', color: '#6b7280', label: 'Neutral' },
            { dataKey: 'negative', color: '#ef4444', label: 'Negative' },
          ]}
        />
      </div>

      {/* Section D: Analyses */}
      <div>
        {/* Tab bar + View toggle */}
        <div className="flex items-center justify-between mb-4 border-b border-gray-200">
          <div className="flex items-center space-x-1">
            {ANALYSIS_TABS.map(tab => {
              const Icon = tab.icon;
              const isActive = analysisTab === tab.key;
              return (
                <button
                  key={tab.key}
                  onClick={() => { setAnalysisTab(tab.key); setPage(1); }}
                  className={`flex items-center space-x-1.5 ${isActive ? 'tab-active' : 'tab'}`}
                >
                  {Icon && <Icon size={14} />}
                  <span>{tab.label}</span>
                  {tab.key === 'negative' && negativeCount > 0 && (
                    <span className="ml-1 text-xs bg-red-100 text-red-700 px-1.5 py-0.5 rounded-full">{negativeCount}</span>
                  )}
                </button>
              );
            })}
          </div>
          {/* Grid / List toggle */}
          <div className="flex items-center space-x-1 pb-2">
            <button
              onClick={() => setViewMode('grid')}
              className={`p-1.5 rounded transition-colors ${viewMode === 'grid' ? 'bg-primary-100 text-primary-700' : 'text-gray-400 hover:text-gray-600'}`}
              title="Grid view"
            >
              <LayoutGrid size={16} />
            </button>
            <button
              onClick={() => setViewMode('list')}
              className={`p-1.5 rounded transition-colors ${viewMode === 'list' ? 'bg-primary-100 text-primary-700' : 'text-gray-400 hover:text-gray-600'}`}
              title="List view"
            >
              <List size={16} />
            </button>
          </div>
        </div>

        {/* Collapsible Signal Library panel */}
        {showSignalLibrary && (
          <div className="card p-5 mb-4">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-semibold text-gray-700">
                Signal Library
                <span className="ml-1.5 text-xs font-normal text-gray-400">({filteredSignals.length})</span>
              </h3>
            </div>

            {/* Category pills */}
            <div className="flex flex-wrap gap-1.5 mb-3">
              {categories.map(cat => (
                <button
                  key={cat}
                  onClick={() => setSignalFilter(cat)}
                  className={`text-xs px-2.5 py-1 rounded-full transition-colors ${
                    signalFilter === cat
                      ? 'bg-primary-600 text-white'
                      : (CATEGORY_COLORS[cat] || 'bg-gray-100 text-gray-600') + ' hover:opacity-80'
                  }`}
                >
                  {toTitleCase(cat)}
                </button>
              ))}
            </div>

            {/* Signal grid */}
            {filteredSignals.length === 0 ? (
              <p className="text-sm text-gray-400 py-2 text-center">No signals in this category.</p>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-2 max-h-64 overflow-y-auto">
                {filteredSignals.map(signal => (
                  <div key={signal.id || signal.signal_name} className="flex items-center justify-between px-3 py-2 bg-gray-50 rounded-lg">
                    <span className="text-sm text-gray-800 truncate mr-2">{toTitleCase(signal.signal_name)}</span>
                    <div className="flex items-center space-x-1.5 flex-shrink-0">
                      <span className={`text-xs px-1.5 py-0.5 rounded ${CATEGORY_COLORS[signal.signal_category] || 'bg-gray-100 text-gray-600'}`}>
                        {toTitleCase(signal.signal_category)}
                      </span>
                      <span className={`w-1.5 h-1.5 rounded-full ${signal.is_active ? 'bg-green-400' : 'bg-gray-300'}`} />
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Analysis cards */}
        {analyses.length === 0 ? (
          <div className="card p-12 text-center">
            <BarChart3 size={36} className="mx-auto text-gray-300 mb-3" />
            <p className="text-sm font-medium text-gray-500">No analyses found for this period</p>
            <p className="text-xs text-gray-400 mt-1.5 max-w-sm mx-auto">
              Sentiment analyses are generated automatically when calls are processed through connected channels. Try adjusting the date range.
            </p>
          </div>
        ) : viewMode === 'grid' ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {analyses.map(analysis => (
              <AnalysisCard
                key={analysis.id}
                analysis={analysis}
                compact
                onViewDetails={setSelectedAnalysis}
              />
            ))}
          </div>
        ) : (
          <div className="space-y-3">
            {analyses.map(analysis => (
              <AnalysisCard
                key={analysis.id}
                analysis={analysis}
                onViewDetails={setSelectedAnalysis}
              />
            ))}
          </div>
        )}

        <Pagination
          page={page}
          pageSize={pageSize}
          total={totalAnalyses}
          onPageChange={setPage}
          onPageSizeChange={setPageSize}
          loading={loading}
        />
      </div>

      {/* Detail Modal */}
      {selectedAnalysis && (() => {
        const idx = analyses.findIndex(a => a.id === selectedAnalysis.id);
        return (
          <AnalysisDetailModal
            analysis={selectedAnalysis}
            onClose={() => setSelectedAnalysis(null)}
            hasPrev={idx > 0}
            hasNext={idx < analyses.length - 1}
            onNavigate={(dir) => {
              const next = analyses[idx + dir];
              if (next) setSelectedAnalysis(next);
            }}
          />
        );
      })()}
    </div>
  );
};

export default SentimentAnalysisPage;
